import { Dialog } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import { TbArrowLeft, TbMoon, TbSun } from "react-icons/tb";
import {
  Link,
  Redirect,
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";
import { PasswordConfirm } from "../../../../Components/Molecules";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { verifyTransactionalPassword } from "../../../../Hooks/UsePix";
import { useToast } from "../../../../Hooks/toast";
import { fetchBaasAccountById } from "../../../../Services/baasAccountsListApi";
import { baasProxy } from "../../../../Services/baasConsoleClient";
import {
  getInstancePixLimits,
  getPixLimits,
  setPixLimits,
} from "../../../../Services/baasPixApi";
import {
  fetchBaasStablesLimitPolicies,
  setBaasStablesAccountLimits,
} from "../../../../Services/baasStablesWalletApi";
import { formatInputValue } from "../../../../Utils/formatInputValue";
import { formatMonetaryValue } from "../../../../Utils/formatters";
import {
  formatBrlMajorMask,
  parseBrlMajorMask,
  resolvePixFeeInstanceName,
} from "../Instances/baasInstanceCreateConstants";
import { getInstancePixFees } from "../../../../Services/baasInstancesApi";
import {
  readPixFeeCatalogInstanceName,
  resolvePixFeeHydrateInstance,
} from "../../../../Utils/hydrateAccountPixFeeSlot";
import {
  PIX_LIMIT_PERIODS,
  resolvePixLimitMeta,
} from "../../Pix/PixLimits/constants";
import {
  BAAS_STABLES_LIMIT_POLICY_TEMPLATES,
  policyDraftKey,
} from "../Stables/baasStablesLimitsConstants";
import { BaasAccountDetailSkeleton } from "../components/BaasSkeleton";
import { hasBaasBankingRails } from "../baasCapabilities";
import {
  BAAS_ACCOUNTS_PATH,
  BAAS_LIMIT_RAIL_IDS,
  buildBaasAccountLimitesConfigPath,
  buildBaasAccountLimitesPath,
  getVisibleBaasLimitRailTabs,
} from "../constants";
import { BaasAccountStatusBadge } from "./components/BaasAccountStatusBadge";
import { BaasAccountOverBalancePanel } from "./components/BaasAccountOverBalancePanel";

const BORDER_CARD =
  "rounded-xl border border-gray-200 bg-white dark:border-brand-dark-border dark:bg-brand-dark-surface";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const inputErrorClass =
  "h-10 w-full rounded-lg border border-red-400 px-3 text-sm text-brand-dark outline-none transition focus:border-red-500 dark:border-red-500 dark:bg-brand-dark-elevated dark:text-gray-100";

/** Empty/null inherit flag = inherit; `false` is a real override (0 is a value). */
const readPixFeeInherit = (slot) => {
  const v = slot?.inherit_fees ?? slot?.inheritFees;
  return v !== false;
};

const readPixFeeNumber = (...values) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return 0;
};

const formatPixFeeMajor = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return formatInputValue("0");
  return formatInputValue(String(Math.round(n * 100)));
};

const slotFeeAmountsEmpty = (slot) => {
  const overlayPct = readPixFeeNumber(
    slot?.instance_fee_percent,
    slot?.instanceFeePercent,
    slot?.fee_percent,
    slot?.feePercent,
  );
  const overlayFixed = readPixFeeNumber(
    slot?.instance_fee_fixed,
    slot?.instanceFeeFixed,
    slot?.fee_fixed,
    slot?.feeFixed,
  );
  const costPct = readPixFeeNumber(
    slot?.instance_cost_percent,
    slot?.instanceCostPercent,
    slot?.cost_percent,
    slot?.costPercent,
  );
  const costFixed = readPixFeeNumber(
    slot?.instance_cost_fixed,
    slot?.instanceCostFixed,
    slot?.cost_fixed,
    slot?.costFixed,
  );
  return overlayPct === 0 && overlayFixed === 0 && costPct === 0 && costFixed === 0;
};

/** Map instance `pix/fees` policy row onto account slot shape for inherit display. */
const mergeInstancePolicyIntoSlot = (slot, policy) => {
  if (!policy) return slot || {};
  const feePercent = readPixFeeNumber(policy.fee_percent, policy.feePercent);
  const feeFixed = readPixFeeNumber(policy.fee_fixed, policy.feeFixed);
  const costPercent = readPixFeeNumber(policy.cost_percent, policy.costPercent);
  const costFixed = readPixFeeNumber(policy.cost_fixed, policy.costFixed);
  return {
    ...(slot || {}),
    inherit_fees: true,
    instance_fee_percent: feePercent,
    instance_fee_fixed: feeFixed,
    instance_cost_percent: costPercent,
    instance_cost_fixed: costFixed,
    fee_percent: feePercent,
    fee_fixed: feeFixed,
    cost_percent: costPercent,
    cost_fixed: costFixed,
  };
};

const applyPixFeeSlotDrafts = (code, slot) => {
  const inherit = readPixFeeInherit(slot);
  const overlayPct = inherit
    ? readPixFeeNumber(slot?.instance_fee_percent, slot?.instanceFeePercent, slot?.fee_percent, slot?.feePercent)
    : readPixFeeNumber(slot?.fee_percent, slot?.feePercent);
  const overlayFixed = inherit
    ? readPixFeeNumber(slot?.instance_fee_fixed, slot?.instanceFeeFixed, slot?.fee_fixed, slot?.feeFixed)
    : readPixFeeNumber(slot?.fee_fixed, slot?.feeFixed);
  return {
    [`${code}__fee`]: formatPixFeeMajor(overlayPct),
    [`${code}__fixed`]: formatPixFeeMajor(overlayFixed),
    [`${code}__cost`]: formatPixFeeMajor(
      readPixFeeNumber(
        slot?.instance_cost_percent,
        slot?.instanceCostPercent,
        slot?.cost_percent,
        slot?.costPercent,
      ),
    ),
    [`${code}__costFixed`]: formatPixFeeMajor(
      readPixFeeNumber(
        slot?.instance_cost_fixed,
        slot?.instanceCostFixed,
        slot?.cost_fixed,
        slot?.costFixed,
      ),
    ),
  };
};

const policyKey = (item) =>
  [
    item?.type ?? "t",
    item?.period ?? "p",
    item?.period_hour_start ?? "s",
    item?.period_hour_end ?? "e",
  ].join("-");

const resolvePeriodGroup = (item) => {
  const period = item?.period;
  if (period === "noturno" || period === 2 || period === "2") return "noturno";
  if (period === "diurno" || period === 1 || period === "1") return "diurno";
  const start = Number(item?.period_hour_start);
  const end = Number(item?.period_hour_end);
  if (Number.isFinite(start) && Number.isFinite(end) && start > end) {
    return "noturno";
  }
  return "diurno";
};

const formatPeriodHours = (hourStart, hourEnd) => {
  const start = Number(hourStart);
  const end = Number(hourEnd);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  const displayEnd = (end + 1) % 24;
  return `${start}h às ${displayEnd}h`;
};

const parseAmountDraft = (value) => parseBrlMajorMask(value);

const fromMajor = (reais) => {
  const n = Number(reais);
  if (!Number.isFinite(n)) return "—";
  return formatMonetaryValue(n, "BRL");
};

const policyPeriodKey = (item) => `${item?.type ?? "t"}-${item?.period ?? "p"}`;

const buildCeilingMap = (instanceItems) => {
  const byFull = {};
  const byPeriod = {};
  for (const item of instanceItems || []) {
    const amount = Number(item.amount);
    const monthlyRaw = Number(item.monthly_amount);
    const perOpRaw = Number(item.per_operation_amount);
    const entry = {
      amount: Number.isFinite(amount) ? amount : NaN,
      monthly:
        Number.isFinite(monthlyRaw) && monthlyRaw > 0
          ? monthlyRaw
          : Number.isFinite(amount)
            ? amount * 20
            : NaN,
      perop:
        Number.isFinite(perOpRaw) && perOpRaw > 0
          ? perOpRaw
          : Number.isFinite(amount)
            ? amount
            : NaN,
      allocatedTotal: Number(item.allocated_total),
    };
    byFull[policyKey(item)] = entry;
    byPeriod[policyPeriodKey(item)] = entry;
  }
  return { byFull, byPeriod };
};

const resolveCeilingEntry = (item, ceilingMap) =>
  ceilingMap?.byFull?.[policyKey(item)] ||
  ceilingMap?.byPeriod?.[policyPeriodKey(item)] ||
  null;

const resolveAmountMaxAllowed = (item, ceilingEntry) => {
  const ceiling = Number(item?.instance_ceiling ?? ceilingEntry?.amount);
  if (!Number.isFinite(ceiling) || ceiling < 0) return null;
  const allocatedTotal = Number(
    item?.allocated_total ?? ceilingEntry?.allocatedTotal,
  );
  const allocatedThis = Number(item?.allocated_this_account ?? item?.amount ?? 0);
  const others = Number.isFinite(allocatedTotal)
    ? Math.max(0, allocatedTotal - (Number.isFinite(allocatedThis) ? allocatedThis : 0))
    : 0;
  return Math.max(0, ceiling - others);
};

const FIELD_CEILING_KEY = {
  amount: "amount",
  monthly: "monthly",
  perop: "perop",
};

const resolveFieldMaxAllowed = (item, ceilingEntry, field) => {
  if (field === "amount") return resolveAmountMaxAllowed(item, ceilingEntry);
  const ceiling = Number(ceilingEntry?.[FIELD_CEILING_KEY[field]]);
  if (!Number.isFinite(ceiling) || ceiling < 0) return null;
  return ceiling;
};

const validatePixDraftField = (item, draftKey, field, drafts, ceilingMap) => {
  const amountReais = parseAmountDraft(drafts[draftKey]);
  const meta = resolvePixLimitMeta(item);
  if (!Number.isFinite(amountReais) || amountReais <= 0) {
    return intl.get("baas.pix.limits.invalidAmount");
  }
  const ceilingEntry = resolveCeilingEntry(item, ceilingMap);
  const maxAllowed = resolveFieldMaxAllowed(item, ceilingEntry, field);
  if (maxAllowed != null && amountReais > maxAllowed + 0.009) {
    const fieldLabel =
      field === "monthly"
        ? intl.get("baas.pix.limits.monthlyLabel")
        : field === "perop"
          ? intl.get("baas.pix.limits.perOpLabel")
          : intl.get("baas.pix.limits.dailyLabel");
    return intl.get("baas.pix.limits.exceedsInstance", {
      label: `${meta.label} — ${fieldLabel}`,
      max: fromMajor(maxAllowed),
    });
  }
  return null;
};

const collectPixDraftErrors = (items, drafts, ceilingMap) => {
  const nextErrors = {};
  for (const item of items) {
    const key = policyKey(item);
    const monthlyKey = `${key}:monthly`;
    const perOpKey = `${key}:perop`;
    const amountError = validatePixDraftField(item, key, "amount", drafts, ceilingMap);
    const monthlyError = validatePixDraftField(
      item,
      monthlyKey,
      "monthly",
      drafts,
      ceilingMap,
    );
    const perOpError = validatePixDraftField(item, perOpKey, "perop", drafts, ceilingMap);
    if (amountError) nextErrors[key] = amountError;
    if (monthlyError) nextErrors[monthlyKey] = monthlyError;
    if (perOpError) nextErrors[perOpKey] = perOpError;
  }
  return nextErrors;
};

const resolveRailFromSearch = (search, visibleIds = BAAS_LIMIT_RAIL_IDS) => {
  const rail = new URLSearchParams(search).get("rail");
  if (rail && visibleIds.includes(rail)) return rail;
  return visibleIds[0] || "stables";
};

const LimitsComingSoon = () => (
  <div
    className={`${BORDER_CARD} flex min-h-[220px] flex-col items-center justify-center px-6 py-10 text-center`}
  >
    <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
      {intl.get("baas.accounts.detail.limites.comingSoonTitle")}
    </p>
    <p className="mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
      {intl.get("baas.accounts.detail.limites.comingSoonBody")}
    </p>
  </div>
);

/**
 * Dedicated full-page account limits configuration by payment rail.
 * Route: `/baas/contas/:accountId/limites/configurar?rail=pix|stables|swift|transfers`
 * Password confirmation stays modal-only; Pix / Stables forms are the page body.
 */
export const BaasAccountLimitsConfigPage = () => {
  const { accountId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { addToast } = useToast();
  const { selectedInstance, selectAccount } = useBaasInstance();
  const pageLoading = useBaasPageLoading(accountId);
  const { can, capabilities, loading: capabilitiesLoading } = useBaasCapabilities();
  const allowed = can("accounts.view");
  const canViewPix = can("processing.pix.view");
  const canViewStables = can("processing.stables.view");
  const canConfigureLimits = can("settings.write") || can("processing.pix.write");
  const visibleRailTabs = useMemo(
    () => getVisibleBaasLimitRailTabs({ canViewPix, canViewStables }),
    [canViewPix, canViewStables],
  );
  const visibleRailIds = useMemo(
    () => visibleRailTabs.map((tab) => tab.id),
    [visibleRailTabs],
  );

  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(true);
  const [railTab, setRailTab] = useState(() =>
    resolveRailFromSearch(location.search),
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [policyItems, setPolicyItems] = useState([]);
  const [draftByKey, setDraftByKey] = useState({});
  const [ceilingByKey, setCeilingByKey] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  /** @type {[{ rail: string, policies: Array }|null, Function]} */
  const [pendingSave, setPendingSave] = useState(null);
  const [pixFeePayerEnviado, setPixFeePayerEnviado] = useState("customer");
  const [pixFeePayerRecebido, setPixFeePayerRecebido] = useState("customer");
  const [pixFeeInheritEnviado, setPixFeeInheritEnviado] = useState(true);
  const [pixFeeInheritRecebido, setPixFeeInheritRecebido] = useState(true);
  const [pixFeeDrafts, setPixFeeDrafts] = useState({});
  const [pixFeePayerSaving, setPixFeePayerSaving] = useState(false);
  const [pixFeeCatalogName, setPixFeeCatalogName] = useState(null);

  const limitesPath = buildBaasAccountLimitesPath(accountId, railTab);
  const pixFeeInstanceName = resolvePixFeeInstanceName(
    pixFeeCatalogName
      || selectedInstance?.displayName
      || selectedInstance?.name,
    intl.get("baas.instances.createPage.instanceFallbackName"),
  );
  const pixFeeInstanceVars = { instanceName: pixFeeInstanceName };

  useEffect(() => {
    if (accountId) selectAccount?.(accountId);
  }, [accountId, selectAccount]);

  useEffect(() => {
    setRailTab(resolveRailFromSearch(location.search, visibleRailIds));
  }, [location.search, visibleRailIds]);

  useEffect(() => {
    if (visibleRailIds.includes(railTab)) return;
    const fallback = visibleRailIds[0];
    if (!fallback || !accountId) return;
    setRailTab(fallback);
    history.replace(buildBaasAccountLimitesConfigPath(accountId, fallback));
  }, [accountId, history, railTab, visibleRailIds]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance || !accountId) {
      setAccount(null);
      setAccountLoading(false);
      setPixFeeCatalogName(null);
      return undefined;
    }

    setAccountLoading(true);
    (async () => {
      try {
        const next = await fetchBaasAccountById(selectedInstance, accountId);
        if (!cancelled) setAccount(next);
        if (!cancelled && selectedInstance && accountId) {
          try {
            const payer = await baasProxy({
              instance: selectedInstance,
              method: "GET",
              baasPath: `v1/accounts/${encodeURIComponent(accountId)}/pix-fee-payer`,
            });
            const payerRoot = payer?.data && typeof payer.data === "object" ? payer.data : payer;
            let enviadoSlot = payerRoot?.enviado;
            let recebidoSlot = payerRoot?.recebido;
            const enviado = String(enviadoSlot?.effective ?? enviadoSlot?.Effective ?? "customer")
              .trim()
              .toLowerCase();
            const recebido = String(recebidoSlot?.effective ?? recebidoSlot?.Effective ?? "customer")
              .trim()
              .toLowerCase();
            const inheritEnviado = readPixFeeInherit(enviadoSlot);
            const inheritRecebido = readPixFeeInherit(recebidoSlot);

            // Fallback: if account inherits and BE returned empty catalog,
            // hydrate overlays/costs from the owner instance price list.
            if (
              (inheritEnviado && slotFeeAmountsEmpty(enviadoSlot))
              || (inheritRecebido && slotFeeAmountsEmpty(recebidoSlot))
            ) {
              try {
                const instanceFees = await getInstancePixFees(
                  resolvePixFeeHydrateInstance(
                    selectedInstance,
                    enviadoSlot || recebidoSlot,
                  ),
                );
                const policies = Array.isArray(instanceFees?.policies)
                  ? instanceFees.policies
                  : [];
                const byOp = Object.fromEntries(
                  policies.map((item) => {
                    const op = String(item?.operation_type || item?.operationType || "")
                      .trim()
                      .toLowerCase();
                    return [op, item];
                  }),
                );
                if (inheritEnviado && slotFeeAmountsEmpty(enviadoSlot) && byOp.enviado) {
                  enviadoSlot = mergeInstancePolicyIntoSlot(enviadoSlot, byOp.enviado);
                }
                if (inheritRecebido && slotFeeAmountsEmpty(recebidoSlot) && byOp.recebido) {
                  recebidoSlot = mergeInstancePolicyIntoSlot(recebidoSlot, byOp.recebido);
                }
              } catch {
                // Keep payer payload as-is when instance fees are unavailable.
              }
            }

            if (!cancelled) {
              setPixFeeCatalogName(
                readPixFeeCatalogInstanceName(enviadoSlot)
                  || readPixFeeCatalogInstanceName(recebidoSlot),
              );
              setPixFeePayerEnviado(enviado === "instance" ? "instance" : "customer");
              setPixFeePayerRecebido(recebido === "instance" ? "instance" : "customer");
              setPixFeeInheritEnviado(inheritEnviado);
              setPixFeeInheritRecebido(inheritRecebido);
              setPixFeeDrafts({
                ...applyPixFeeSlotDrafts("enviado", enviadoSlot),
                ...applyPixFeeSlotDrafts("recebido", recebidoSlot),
              });
            }
          } catch {
            if (!cancelled) {
              setPixFeeCatalogName(null);
              setPixFeePayerEnviado("customer");
              setPixFeePayerRecebido("customer");
              setPixFeeInheritEnviado(true);
              setPixFeeInheritRecebido(true);
              setPixFeeDrafts({
                ...applyPixFeeSlotDrafts("enviado", null),
                ...applyPixFeeSlotDrafts("recebido", null),
              });
            }
          }
        }
      } catch {
        if (!cancelled) setAccount(null);
      } finally {
        if (!cancelled) setAccountLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, accountId]);

  // Depend on a stable instance key (not the object): context often rebuilds
  // `selectedInstance` when the instances list refreshes, which would re-fetch and
  // wipe in-progress drafts before save — toast OK with the previous values.
  const selectedInstanceRef = useRef(selectedInstance);
  selectedInstanceRef.current = selectedInstance;
  const selectedInstanceKey = selectedInstance
    ? `${Number(selectedInstance.baasInstanceId ?? selectedInstance.id)}:${String(selectedInstance.environment || "").toLowerCase()}`
    : null;

  const loadPixData = useCallback(async () => {
    const instance = selectedInstanceRef.current;
    if (!instance || !selectedInstanceKey || !accountId || !canViewPix) {
      setPolicyItems([]);
      setDraftByKey({});
      setCeilingByKey({});
      return;
    }
    setLoading(true);
    try {
      const [accountLimits, instanceLimits] = await Promise.all([
        getPixLimits(instance, accountId, { resolved: false }),
        getInstancePixLimits(instance).catch(() => null),
      ]);
      const items = accountLimits?.items || [];
      setPolicyItems(items);
      const drafts = {};
      items.forEach((item) => {
        drafts[`${policyKey(item)}`] =
          item?.amount != null ? formatBrlMajorMask(Number(item.amount)) : "";
        drafts[`${policyKey(item)}:monthly`] =
          item?.monthly_amount != null
            ? formatBrlMajorMask(Number(item.monthly_amount))
            : formatBrlMajorMask(Number(item.amount || 0) * 20);
        drafts[`${policyKey(item)}:perop`] =
          item?.per_operation_amount != null
            ? formatBrlMajorMask(Number(item.per_operation_amount))
            : formatBrlMajorMask(Number(item.amount || 0));
      });
      setDraftByKey(drafts);
      const nextCeiling = buildCeilingMap(instanceLimits?.items);
      setCeilingByKey(nextCeiling);
      setFieldErrors(collectPixDraftErrors(items, drafts, nextCeiling));
    } catch (error) {
      setPolicyItems([]);
      setDraftByKey({});
      setCeilingByKey({});
      addToast({
        type: "error",
        title: error?.message || intl.get("baas.pix.error.load"),
      });
    } finally {
      setLoading(false);
    }
  }, [selectedInstanceKey, accountId, canViewPix, addToast]);

  const loadStablesData = useCallback(async () => {
    const instance = selectedInstanceRef.current;
    if (!instance || !selectedInstanceKey || !accountId || !canViewStables) {
      setDraftByKey({});
      setFieldErrors({});
      return;
    }
    setLoading(true);
    try {
      const configured = await fetchBaasStablesLimitPolicies(
        instance,
        accountId,
      );
      const byKey = Object.fromEntries(
        (configured?.policies || []).map((item) => [
          policyDraftKey(item.operationType, item.periodType),
          item,
        ]),
      );
      const drafts = {};
      BAAS_STABLES_LIMIT_POLICY_TEMPLATES.forEach((template) => {
        const match =
          byKey[policyDraftKey(template.operationType, template.periodType)];
        const cents = Number(match?.limitAmountCents ?? 0);
        drafts[template.key] =
          Number.isFinite(cents) && cents > 0 ? formatBrlMajorMask(cents / 100) : "";
      });
      setDraftByKey(drafts);
      setFieldErrors({});
    } catch (error) {
      setDraftByKey({});
      addToast({
        type: "error",
        title: error?.message || intl.get("baas.stablesWallet.loadFailed"),
      });
    } finally {
      setLoading(false);
    }
  }, [selectedInstanceKey, accountId, canViewStables, addToast]);

  useEffect(() => {
    if (railTab === "pix") loadPixData();
    if (railTab === "stables") loadStablesData();
  }, [railTab, loadPixData, loadStablesData]);

  const policiesByPeriod = useMemo(() => {
    const groups = { diurno: [], noturno: [] };
    for (const item of policyItems) {
      groups[resolvePeriodGroup(item)].push(item);
    }
    return groups;
  }, [policyItems]);

  const handleRailChange = (nextRail) => {
    if (nextRail === railTab) return;
    setRailTab(nextRail);
    setFieldErrors({});
    history.replace(buildBaasAccountLimitesConfigPath(accountId, nextRail));
  };

  const handleDraftChange = (item, draftKey, field, value) => {
    const formatted = formatInputValue(String(value ?? ""));
    setDraftByKey((prev) => {
      const nextDrafts = { ...prev, [draftKey]: formatted };
      const message = validatePixDraftField(
        item,
        draftKey,
        field,
        nextDrafts,
        ceilingByKey,
      );
      setFieldErrors((prevErrors) => {
        if (message) return { ...prevErrors, [draftKey]: message };
        if (!prevErrors[draftKey]) return prevErrors;
        const next = { ...prevErrors };
        delete next[draftKey];
        return next;
      });
      return nextDrafts;
    });
  };

  const validateDraftField = (item, draftKey, field, drafts) =>
    validatePixDraftField(item, draftKey, field, drafts, ceilingByKey);

  const handleDraftBlur = (item, draftKey, field) => {
    setDraftByKey((prev) => {
      const message = validateDraftField(item, draftKey, field, prev);
      setFieldErrors((prevErrors) => {
        if (message) return { ...prevErrors, [draftKey]: message };
        if (!prevErrors[draftKey]) return prevErrors;
        const next = { ...prevErrors };
        delete next[draftKey];
        return next;
      });
      return prev;
    });
  };

  const buildPixPoliciesOrShowErrors = () => {
    if (policyItems.length === 0) {
      addToast({
        type: "error",
        title: intl.get("baas.accounts.detail.limites.empty"),
      });
      return null;
    }

    const nextErrors = {};
    const policies = [];

    for (const item of policyItems) {
      const key = policyKey(item);
      const monthlyKey = `${key}:monthly`;
      const perOpKey = `${key}:perop`;

      const amountError = validateDraftField(item, key, "amount", draftByKey);
      const monthlyError = validateDraftField(item, monthlyKey, "monthly", draftByKey);
      const perOpError = validateDraftField(item, perOpKey, "perop", draftByKey);
      if (amountError) nextErrors[key] = amountError;
      if (monthlyError) nextErrors[monthlyKey] = monthlyError;
      if (perOpError) nextErrors[perOpKey] = perOpError;
      if (amountError || monthlyError || perOpError) continue;

      const amountReais = parseAmountDraft(draftByKey[key]);
      const monthlyReais = parseAmountDraft(draftByKey[monthlyKey]);
      const perOpReais = parseAmountDraft(draftByKey[perOpKey]);

      policies.push({
        type: Number(item.type ?? 0),
        period: Number(item.period ?? 0),
        period_hour_start: Number(item.period_hour_start ?? 6),
        // Day window ends at inclusive hour 19 (not 20); night at 5 — must match SoftReplace keys.
        period_hour_end: Number(
          item.period_hour_end
            ?? (resolvePeriodGroup(item) === "noturno" ? 5 : 19),
        ),
        amount: amountReais,
        monthly_amount: Number.isFinite(monthlyReais) && monthlyReais > 0 ? monthlyReais : amountReais * 20,
        per_operation_amount: Number.isFinite(perOpReais) && perOpReais > 0 ? perOpReais : amountReais,
      });
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      addToast({
        type: "error",
        title: intl.get("baas.pix.limits.validationFailed"),
      });
      return null;
    }

    return policies;
  };

  const buildStablesPoliciesOrShowErrors = () => {
    const nextErrors = {};
    const policies = [];

    for (const template of BAAS_STABLES_LIMIT_POLICY_TEMPLATES) {
      const amountReais = parseAmountDraft(draftByKey[template.key]);
      if (!Number.isFinite(amountReais) || amountReais <= 0) {
        nextErrors[template.key] = intl.get("baas.pix.limits.invalidAmount");
        continue;
      }
      policies.push({
        operation_type: template.operationType,
        period_type: template.periodType,
        limit_amount_cents: Math.round(amountReais * 100),
      });
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      addToast({
        type: "error",
        title: intl.get("baas.pix.limits.validationFailed"),
      });
      return null;
    }

    // Soft-replace only the hourly rows from the form. Omitting daily/monthly
    // deactivates them so GET …/limits (daily-preferring) does not keep showing
    // a stale daily ceiling after the user adjusts the hourly form.
    return policies;
  };

  const handleProceedToPassword = () => {
    const policies =
      railTab === "stables"
        ? buildStablesPoliciesOrShowErrors()
        : buildPixPoliciesOrShowErrors();
    if (!policies) return;
    setPendingSave({ rail: railTab, policies });
    setPasswordError(null);
    setPasswordOpen(true);
  };

  const closePasswordModal = () => {
    if (saving) return;
    setPasswordOpen(false);
    setPasswordError(null);
    setPendingSave(null);
  };

  const persistPolicies = async ({ rail, policies }) => {
    if (rail === "stables") {
      await setBaasStablesAccountLimits(selectedInstance, accountId, { policies });
      addToast({
        type: "success",
        title: intl.get("baas.stablesWallet.limits.saveSuccess"),
      });
    } else {
      await setPixLimits(selectedInstance, accountId, { policies });
      addToast({ type: "success", title: intl.get("baas.pix.limits.saveSuccess") });
    }
    history.push(limitesPath);
  };

  const handleSubmitPassword = async (passwordCombination) => {
    if (!selectedInstance || !accountId || !pendingSave || saving) return;

    setSaving(true);
    setPasswordError(null);

    const verify = await verifyTransactionalPassword({ passwordCombination });
    if (!verify.success) {
      setSaving(false);
      setPasswordError({
        title: verify.message || intl.get("passwordConfirm.warning"),
        description:
          verify.description ||
          intl.get("passwordConfirm.invalidTransactionPassword"),
      });
      return;
    }

    try {
      await persistPolicies(pendingSave);
    } catch (error) {
      const detail =
        error?.detail ||
        error?.message ||
        intl.get("baas.pix.error.limits");
      if (error?.code === "limits_exceed_instance") {
        addToast({
          type: "error",
          title: intl.get("baas.pix.limits.exceedsInstanceServer"),
          description: detail,
        });
        setPasswordOpen(false);
      } else {
        setPasswordError({
          title:
            pendingSave.rail === "stables"
              ? intl.get("baas.stablesWallet.limits.saveError")
              : intl.get("baas.pix.error.limits"),
          description: detail,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSavePixFeePayer = async () => {
    if (!selectedInstance || !accountId || pixFeePayerSaving) return;
    setPixFeePayerSaving(true);
    const parseFee = (key) => {
      const n = parseBrlMajorMask(pixFeeDrafts[key]);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    };
    try {
      const saved = await baasProxy({
        instance: selectedInstance,
        method: "PATCH",
        baasPath: `v1/accounts/${encodeURIComponent(accountId)}/pix-fee-payer`,
        body: {
          enviado: pixFeePayerEnviado,
          recebido: pixFeePayerRecebido,
          enviado_fees: pixFeeInheritEnviado
            ? { inherit: true }
            : {
                inherit: false,
                fee_percent: parseFee("enviado__fee"),
                fee_fixed: parseFee("enviado__fixed"),
              },
          recebido_fees: pixFeeInheritRecebido
            ? { inherit: true }
            : {
                inherit: false,
                fee_percent: parseFee("recebido__fee"),
                fee_fixed: parseFee("recebido__fixed"),
              },
        },
      });
      const payer = saved || (await baasProxy({
        instance: selectedInstance,
        method: "GET",
        baasPath: `v1/accounts/${encodeURIComponent(accountId)}/pix-fee-payer`,
      }));
      if (payer) {
        setPixFeeInheritEnviado(readPixFeeInherit(payer?.enviado));
        setPixFeeInheritRecebido(readPixFeeInherit(payer?.recebido));
        setPixFeeDrafts({
          ...applyPixFeeSlotDrafts("enviado", payer?.enviado),
          ...applyPixFeeSlotDrafts("recebido", payer?.recebido),
        });
      }
      addToast({
        type: "success",
        title: intl.get("baas.accounts.detail.limites.pixFeePayerSaved"),
      });
    } catch (error) {
      addToast({
        type: "error",
        title: error?.message || intl.get("baas.accounts.detail.limites.pixFeePayerSaved"),
      });
    } finally {
      setPixFeePayerSaving(false);
    }
  };

  const renderPolicyRow = (item) => {
    const key = policyKey(item);
    const monthlyKey = `${key}:monthly`;
    const perOpKey = `${key}:perop`;
    const meta = resolvePixLimitMeta(item);
    const Icon = meta.icon;
    const hours =
      item.period_hours ||
      formatPeriodHours(item.period_hour_start, item.period_hour_end);
    const ceilingEntry = resolveCeilingEntry(item, ceilingByKey);
    const amountCeiling = Number(item.instance_ceiling ?? ceilingEntry?.amount);
    const hasAmountError = Boolean(fieldErrors[key]);
    const hasMonthlyError = Boolean(fieldErrors[monthlyKey]);
    const hasPerOpError = Boolean(fieldErrors[perOpKey]);
    const draftAmount =
      parseBrlMajorMask(draftByKey[key]) || Number(item.amount) || 0;
    const allocatedTotal = Number(
      item.allocated_total ?? ceilingEntry?.allocatedTotal,
    );
    const allocatedThis = Number(item.allocated_this_account ?? item.amount ?? 0);
    const others = Number.isFinite(allocatedTotal)
      ? Math.max(0, allocatedTotal - (Number.isFinite(allocatedThis) ? allocatedThis : 0))
      : 0;
    const remaining = Number.isFinite(amountCeiling)
      ? Math.max(0, amountCeiling - (others + draftAmount))
      : null;
    const alreadyOver = Boolean(item.exceeds_ceiling) || remaining === 0 && draftAmount > amountCeiling;

    return (
      <div
        key={key}
        className="flex flex-col gap-3 rounded-lg bg-gray-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between dark:bg-brand-dark-elevated"
      >
        <div className="flex min-w-0 items-start gap-2">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white dark:bg-brand-dark-surface">
            <Icon className="h-4 w-4 text-[#e7a73f]" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
              {meta.label}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {[meta.description, hours].filter(Boolean).join(" · ")}
            </p>
            {Number.isFinite(amountCeiling) ? (
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                {intl.get("baas.pix.limits.instanceCeilingUsage", {
                  max: fromMajor(amountCeiling),
                  account: fromMajor(draftAmount),
                  remaining: fromMajor(remaining),
                })}
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                {intl.get("baas.pix.limits.instanceUnlimited")}
              </p>
            )}
            {alreadyOver ? (
              <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">
                {intl.get("baas.pix.limits.legacyExceedsCeiling")}
              </p>
            ) : null}
            {hasAmountError || hasMonthlyError || hasPerOpError ? (
              <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">
                {fieldErrors[key] || fieldErrors[monthlyKey] || fieldErrors[perOpKey]}
              </p>
            ) : null}
          </div>
        </div>
        <div className="grid w-full shrink-0 gap-2 sm:w-56">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.pix.limits.dailyLabel")}
            </label>
            <input
              className={hasAmountError ? inputErrorClass : inputClass}
              value={draftByKey[key] ?? ""}
              onChange={(event) =>
                handleDraftChange(item, key, "amount", event.target.value)
              }
              onBlur={() => handleDraftBlur(item, key, "amount")}
              placeholder="0,00"
              inputMode="decimal"
              disabled={loading || saving}
              aria-invalid={hasAmountError}
              aria-label={`${meta.label} — ${intl.get("baas.pix.limits.dailyLabel")}`}
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.pix.limits.monthlyLabel")}
            </label>
            <input
              className={hasMonthlyError ? inputErrorClass : inputClass}
              value={draftByKey[monthlyKey] ?? ""}
              onChange={(event) =>
                handleDraftChange(item, monthlyKey, "monthly", event.target.value)
              }
              onBlur={() => handleDraftBlur(item, monthlyKey, "monthly")}
              placeholder="0,00"
              inputMode="decimal"
              disabled={loading || saving}
              aria-invalid={hasMonthlyError}
              aria-label={`${meta.label} — ${intl.get("baas.pix.limits.monthlyLabel")}`}
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.pix.limits.perOpLabel")}
            </label>
            <input
              className={hasPerOpError ? inputErrorClass : inputClass}
              value={draftByKey[perOpKey] ?? ""}
              onChange={(event) =>
                handleDraftChange(item, perOpKey, "perop", event.target.value)
              }
              onBlur={() => handleDraftBlur(item, perOpKey, "perop")}
              placeholder="0,00"
              inputMode="decimal"
              disabled={loading || saving}
              aria-invalid={hasPerOpError}
              aria-label={`${meta.label} — ${intl.get("baas.pix.limits.perOpLabel")}`}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderPixForm = () => {
    if (!canViewPix) {
      return (
        <div
          className={`${BORDER_CARD} px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400`}
        >
          {intl.get("baas.home.capabilities.permissionDenied")}
        </div>
      );
    }

    return (
      <div className={`${BORDER_CARD} space-y-5 p-4 sm:p-5`}>
        <div>
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("baas.accounts.detail.limites.tabs.pix")}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.pix.limits.modalHint")}
          </p>
        </div>

        <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/80 p-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40">
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("baas.accounts.detail.limites.pixFeePayerTitle")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.accounts.detail.limites.pixFeePayerHint")}
          </p>
          {[
            {
              code: "enviado",
              value: pixFeePayerEnviado,
              setValue: setPixFeePayerEnviado,
              inherit: pixFeeInheritEnviado,
              setInherit: setPixFeeInheritEnviado,
              labelKey: "baas.accounts.detail.limites.pixFeePayerEnviado",
            },
            {
              code: "recebido",
              value: pixFeePayerRecebido,
              setValue: setPixFeePayerRecebido,
              inherit: pixFeeInheritRecebido,
              setInherit: setPixFeeInheritRecebido,
              labelKey: "baas.accounts.detail.limites.pixFeePayerRecebido",
            },
          ].map((row) => (
            <div key={row.code} className="space-y-2 border-t border-gray-200 pt-3 first:border-t-0 first:pt-0 dark:border-brand-dark-border">
              <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                {intl.get(row.labelKey)}
              </p>
              <div className="flex flex-wrap gap-3 text-sm text-brand-dark dark:text-gray-100">
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="radio"
                    name={`account-pix-fee-payer-${row.code}`}
                    checked={row.value === "customer"}
                    onChange={() => row.setValue("customer")}
                    disabled={loading || saving || pixFeePayerSaving}
                  />
                  {intl.get("baas.accounts.detail.limites.pixFeePayerCustomer")}
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="radio"
                    name={`account-pix-fee-payer-${row.code}`}
                    checked={row.value === "instance"}
                    onChange={() => row.setValue("instance")}
                    disabled={loading || saving || pixFeePayerSaving}
                  />
                  {intl.get("baas.accounts.detail.limites.pixFeePayerInstance")}
                </label>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {intl.get("baas.accounts.detail.limites.pixFeePayerWhoPaysHint")}
              </p>
              <label className="flex items-center gap-2 text-xs text-brand-dark dark:text-gray-100">
                <input
                  type="checkbox"
                  checked={row.inherit}
                  onChange={(event) => row.setInherit(event.target.checked)}
                  disabled={loading || saving || pixFeePayerSaving}
                />
                {intl.get("baas.accounts.detail.limites.pixFeeInherit", pixFeeInstanceVars)}
              </label>
              <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                {intl.get("baas.accounts.detail.limites.costValutGroup", pixFeeInstanceVars)}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {intl.get("baas.instances.createPage.pixFeeCostHint", pixFeeInstanceVars)}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[11px] text-gray-500 dark:text-gray-400">
                    {intl.get("baas.instances.createPage.costPercentLabel")}
                  </label>
                    <input
                    className={inputClass}
                    value={pixFeeDrafts[`${row.code}__cost`] ?? ""}
                    readOnly
                    disabled
                    inputMode="decimal"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] text-gray-500 dark:text-gray-400">
                    {intl.get("baas.instances.createPage.costFixedLabel")}
                  </label>
                  <input
                    className={inputClass}
                    value={pixFeeDrafts[`${row.code}__costFixed`] ?? ""}
                    readOnly
                    disabled
                    inputMode="decimal"
                  />
                </div>
              </div>
              <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                {intl.get("baas.accounts.detail.limites.overlayGroup")}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[11px] text-gray-500 dark:text-gray-400">
                    {intl.get("baas.instances.createPage.feePercentLabel")}
                  </label>
                  <input
                    className={inputClass}
                    value={pixFeeDrafts[`${row.code}__fee`] ?? ""}
                    onChange={(event) =>
                      setPixFeeDrafts((prev) => ({
                        ...prev,
                        [`${row.code}__fee`]: formatInputValue(String(event.target.value ?? "")),
                      }))
                    }
                    disabled={row.inherit || loading || saving || pixFeePayerSaving}
                    inputMode="decimal"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] text-gray-500 dark:text-gray-400">
                    {intl.get("baas.instances.createPage.feeFixedLabel")}
                  </label>
                  <input
                    className={inputClass}
                    value={pixFeeDrafts[`${row.code}__fixed`] ?? ""}
                    onChange={(event) =>
                      setPixFeeDrafts((prev) => ({
                        ...prev,
                        [`${row.code}__fixed`]: formatInputValue(String(event.target.value ?? "")),
                      }))
                    }
                    disabled={row.inherit || loading || saving || pixFeePayerSaving}
                    inputMode="decimal"
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="btn-secondary !h-9 !text-xs"
            onClick={handleSavePixFeePayer}
            disabled={loading || saving || pixFeePayerSaving}
          >
            {pixFeePayerSaving
              ? intl.get("baas.settings.saving")
              : intl.get("baas.accounts.detail.limites.pixFeePayerSave")}
          </button>
        </div>

        {loading ? (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.pix.limits.loading")}
          </p>
        ) : policyItems.length === 0 ? (
          <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:bg-brand-dark-elevated dark:text-gray-400">
            {intl.get("baas.accounts.detail.limites.empty")}
          </p>
        ) : (
          <div className="space-y-6">
            {["diurno", "noturno"].map((groupId) => {
              const items = policiesByPeriod[groupId];
              if (!items.length) return null;
              const Icon = groupId === "noturno" ? TbMoon : TbSun;
              const sample = items[0];
              const hours =
                formatPeriodHours(
                  sample?.period_hour_start,
                  sample?.period_hour_end,
                ) || PIX_LIMIT_PERIODS[groupId]?.hours;
              const title =
                groupId === "noturno"
                  ? intl.get("baas.accounts.detail.limites.periodNight")
                  : intl.get("baas.accounts.detail.limites.periodDay");

              return (
                <section key={groupId} className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Icon className="h-4 w-4 text-[#e7a73f]" aria-hidden />
                    <h3 className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                      {title}
                    </h3>
                    {hours ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({hours})
                      </span>
                    ) : null}
                  </div>
                  <div className="grid gap-3">{items.map(renderPolicyRow)}</div>
                </section>
              );
            })}

            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.pix.limits.instanceLinkHint")}{" "}
              <Link
                to="/baas/configuracoes#baas-settings-pix-limits"
                className="font-medium text-[#e7a73f] hover:underline"
              >
                {intl.get("baas.nav.configuracoes")}
              </Link>
              .
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-brand-dark-border">
          <Link to={limitesPath} className="btn-outline">
            {intl.get("baas.accounts.create.cancel")}
          </Link>
          <button
            type="button"
            className="btn-primary"
            onClick={handleProceedToPassword}
            disabled={loading || saving || policyItems.length === 0}
          >
            {intl.get("baas.pix.limits.continuePassword")}
          </button>
        </div>
      </div>
    );
  };

  const renderStablesForm = () => {
    if (!canViewStables) {
      return (
        <div
          className={`${BORDER_CARD} px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400`}
        >
          {intl.get("baas.home.capabilities.permissionDenied")}
        </div>
      );
    }

    return (
      <div className={`${BORDER_CARD} space-y-5 p-4 sm:p-5`}>
        <div>
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("baas.accounts.detail.limites.tabs.stables")}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.stablesWallet.limits.modalHint")}
          </p>
        </div>

        {loading ? (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.pix.limits.loading")}
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {intl.get("baas.stablesWallet.limits.periodDailyMonthly") ||
                "Diário e mensal (America/Sao_Paulo)"}
            </p>
            {BAAS_STABLES_LIMIT_POLICY_TEMPLATES.map((template) => {
              const hasError = Boolean(fieldErrors[template.key]);
              return (
                <div
                  key={template.key}
                  className="flex flex-col gap-3 rounded-lg bg-gray-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between dark:bg-brand-dark-elevated"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                      {intl.get(template.labelKey)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {intl.get(template.descriptionKey)}
                    </p>
                    {hasError ? (
                      <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">
                        {fieldErrors[template.key]}
                      </p>
                    ) : null}
                  </div>
                  <div className="w-full shrink-0 sm:w-40">
                    <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                      {intl.get("baas.pix.limits.amountLabel")}
                    </label>
                    <input
                      className={hasError ? inputErrorClass : inputClass}
                      value={draftByKey[template.key] ?? ""}
                      onChange={(event) => {
                        const formatted = formatInputValue(
                          String(event.target.value ?? ""),
                        );
                        setDraftByKey((prev) => ({
                          ...prev,
                          [template.key]: formatted,
                        }));
                        setFieldErrors((prev) => {
                          if (!prev[template.key]) return prev;
                          const next = { ...prev };
                          delete next[template.key];
                          return next;
                        });
                      }}
                      placeholder="0,00"
                      inputMode="decimal"
                      disabled={loading || saving}
                      aria-invalid={hasError}
                      aria-label={`${intl.get(template.labelKey)} — ${intl.get("baas.pix.limits.amountLabel")}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-brand-dark-border">
          <Link to={limitesPath} className="btn-outline">
            {intl.get("baas.accounts.create.cancel")}
          </Link>
          <button
            type="button"
            className="btn-primary"
            onClick={handleProceedToPassword}
            disabled={loading || saving}
          >
            {intl.get("baas.pix.limits.continuePassword")}
          </button>
        </div>
      </div>
    );
  };

  if (pageLoading || accountLoading || capabilitiesLoading) {
    return <BaasAccountDetailSkeleton />;
  }

  if (!hasBaasBankingRails(capabilities)) {
    return <Redirect to="/baas/inicio" />;
  }

  if (!allowed || !canConfigureLimits || !account) {
    return <Redirect to={BAAS_ACCOUNTS_PATH} />;
  }

  const renderActiveRail = () => {
    if (railTab === "pix") return renderPixForm();
    if (railTab === "stables") return renderStablesForm();
    return <LimitsComingSoon />;
  };

  return (
    <div className="baas-account-limits-config min-w-0 space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#e7a73f]">
            {intl.get("baas.accounts.detail.tabs.limites")}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
              {intl.get("baas.accounts.detail.limites.configTitle")}
            </h1>
            <BaasAccountStatusBadge status={account.status} />
          </div>
          <p className="mt-1 break-all font-mono text-sm text-gray-500 dark:text-gray-400">
            {account.id || accountId}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.accounts.detail.limites.configSubtitle")}
          </p>
        </div>

        <Link
          to={limitesPath}
          className="btn-outline w-auto shrink-0 self-start !text-xs"
        >
          <TbArrowLeft className="h-4 w-4" />
          {intl.get("baas.accounts.detail.limites.configBack")}
        </Link>
      </header>

      <BaasAccountOverBalancePanel accountId={accountId} nested />

      <div
        className="extract-view-tabs extract-view-tabs--inline border-b border-gray-100 dark:border-brand-dark-border"
        role="tablist"
        aria-label={intl.get("baas.accounts.detail.limites.configTitle")}
      >
        {visibleRailTabs.map((tab) => {
          const isActive = railTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`extract-view-tab ${isActive ? "extract-view-tab--active" : ""}`}
              onClick={() => handleRailChange(tab.id)}
            >
              {intl.get(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {renderActiveRail()}

      <Dialog
        open={passwordOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          closePasswordModal();
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: "!rounded-2xl !shadow-card overflow-hidden" }}
      >
        <PasswordConfirm
          variant="embedded"
          open={passwordOpen}
          submitPassword={handleSubmitPassword}
          onBack={closePasswordModal}
          onClose={closePasswordModal}
          error={passwordError}
          loading={saving}
          onDismissError={() => setPasswordError(null)}
          title={intl.get("baas.pix.limits.passwordTitle")}
          subtitle={
            pendingSave?.rail === "stables"
              ? intl.get("baas.stablesWallet.limits.passwordSubtitle")
              : intl.get("baas.pix.limits.passwordSubtitle")
          }
        />
      </Dialog>
    </div>
  );
};

export default BaasAccountLimitsConfigPage;
